import { createContext, useEffect, useState, PropsWithChildren } from 'react'

import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useToastContext } from './ToastContext'
import { useAuthContext } from './AuthContext'
import { useProvideContext } from '../../hooks/context'
import { useImmutableObject } from '../../hooks/immutable'
import { useAction } from '../../hooks/action'
import { ok, throwIfResponseNotOk } from '../../tools/api'
import { EulaDialog, EulaScreen } from '../common/EulaViewer'
import DataBuilder from '../../tools/DataBuilder'

export interface EulaContextValue extends DataContextValue {
    eulaOpen: boolean
    eulaPdf?: string
    eulaExists: boolean
    eulaAccepted: boolean
    acceptEula: () => void
    rejectEula: () => void
    showEula: () => void
    hideEula: () => void
}

const EulaContext = createContext<EulaContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { logout } = useAuthContext()
    const { me, meLoading, organizationsLoading, refreshMe } = useUserContext()
    const { eulaApi, identityApi } = useApiContext()
    const [eulaOpen, setEulaOpen] = useState(false)
    const Toast = useToastContext()

    const [eula, loading, error] = useImmutableObject(async () => {
        if (!me) return
        const response = await eulaApi.getActiveEulaV2()
        if (ok(response)) return response.data
        // NOTE: We accept a missing EULA as a part of normal operation.
        return undefined
    }, [me])

    const eulaAccepted = eula?.id !== null && me?.signedEula?.eulaId === eula?.id
    const data = eula?.base64
    const eulaPdf = !data
        ? undefined
        : new DataBuilder(data)
              .pdf()
              .base64()
              // Rendering embedded PDF with arguments:
              //  https://pdfobject.com/pdf/pdf_open_parameters_acro8.pdf
              .fragment('toolbar', 0)
              .fragment('navpanes', 0)
              .fragment('view', 'FitH')
              .toString()

    const [signLoading, signError, acceptEula] = useAction(async () => {
        if (!eula) return
        const response = await identityApi.selfSignEulaV2({ eulaId: eula.id })
        throwIfResponseNotOk(response)
        refreshMe()
    })

    const [rejectLoading, rejectError, rejectEula] = useAction(async () => {
        const response = await identityApi.selfRevokeEulaV2()
        throwIfResponseNotOk(response)
        logout()
    })

    const showEula = () => setEulaOpen(true)
    const hideEula = () => setEulaOpen(false)

    const eulaExists = !!eulaPdf

    // NOTE: Including a few extra early-stage loading states
    //  to make sure we don't flicker between eula and app view,
    //  before everything is loaded in and we know enough to show the correct view.
    const eulaLoading = loading || meLoading || organizationsLoading
    const eulaError = signError || rejectError
    useEffect(() => {
        if (eulaError) Toast.error(eulaError)
    }, [eulaError])

    const value = {
        eulaOpen,
        eulaPdf,
        eulaAccepted,
        eulaExists,
        acceptEula,
        rejectEula,
        showEula,
        hideEula,
        loading,
        error,
    }

    return (
        <EulaContext.Provider value={value}>
            <EulaScreen
                loading={eulaLoading}
                hasUser={!!me}
                hasEula={!!eula?.id}
                consent={eulaAccepted}
                children={children}
            />
            <EulaDialog />
        </EulaContext.Provider>
    )
}

export const useEulaContext = () => useProvideContext(EulaContext, 'Eula')
